import PageShell from '@/pages/shared/PageShell';

export default function Multilingual() {
  return (
    <PageShell
      title="多言語対応プロジェクト管理ツール｜日英タイ3言語・国別祝日対応｜”おもいむすび”"
      description="日本語・英語・タイ語に対応するプロジェクト管理ツール。メンバーごとにUI言語を切り替え可能。国別祝日を考慮したスケジュール管理で、海外チームとのプロジェクトを効率化します。"
      keywords="多言語 プロジェクト管理ツール,英語対応 プロジェクト管理ツール,タイ語対応 プロジェクト管理ツール,海外拠点 プロジェクト管理,グローバル プロジェクト管理"
      canonical="https://omoimusubi.asia/features/multilingual/"
      h1="日本語・英語・タイ語で使えるプロジェクト管理ツール"
      subtitle="メンバーごとに利用言語や所属国を設定し、国別の祝日・稼働日を考慮したスケジュール管理を実現します。"
      relatedLinks={[
        { label: 'AIガントチャート', path: '/features/ai-gantt/' },
        { label: 'グローバルプロジェクト管理ガイド', path: '/guides/global-project-management/' },
        { label: 'プロジェクトスケジュール管理ガイド', path: '/guides/project-schedule-management/' },
      ]}
    >
      <div className="not-prose mb-8 rounded-xl overflow-hidden border border-background-200/70">
        <img
          src="https://readdy.ai/api/search-image?query=A%20bright%20modern%20Japanese%20office%20with%20two%20colleagues%20a%20Japanese%20woman%20and%20a%20Thai%20woman%20in%20their%2030s%20working%20together%20at%20a%20shared%20desk%20with%20dual%20monitors%20one%20screen%20showing%20Japanese%20language%20project%20management%20interface%20the%20other%20showing%20Thai%20language%20interface%20both%20smiling%20and%20collaborating%20natural%20daylight%20streaming%20through%20large%20windows%20green%20indoor%20plants%20warm%20wooden%20desk%20clean%20refreshing%20multicultural%20contemporary%20workplace%20atmosphere%20professional%20friendly&width=1200&height=700&seq=feature-multilingual-01&orientation=landscape&nocache=true"
          alt="多言語対応の製品画面 - 日本とタイの祝日を別々に管理できるカレンダー画面"
          className="w-full h-auto"
        />
      </div>

      <section>
        <h2>対応言語</h2>
        <div className="not-prose grid grid-cols-1 sm:grid-cols-3 gap-6 my-6">
          {[
            { code: 'JP', lang: '日本語', desc: '日本の祝日・稼働日に対応', flag: '🇯🇵' },
            { code: 'EN', lang: 'English', desc: '国際的な祝日・稼働日に対応', flag: '🇺🇸' },
            { code: 'TH', lang: 'ภาษาไทย', desc: 'タイの祝日・稼働日に対応', flag: '🇹🇭' },
          ].map((l) => (
            <div key={l.code} className="bg-background-100 rounded-lg p-6 border border-background-200/70 text-center">
              <div className="text-3xl mb-3">{l.flag}</div>
              <h3 className="text-base font-bold text-foreground-900 mb-1">{l.lang}</h3>
              <p className="text-xs text-foreground-500">{l.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="not-prose my-8 bg-background-100 rounded-lg border border-background-200/70 p-6">
        <h2 className="text-base font-bold text-foreground-900 mb-4">国別祝日カレンダーのイメージ</h2>
        <div className="overflow-x-auto">
          <div className="min-w-[600px]">
            <div className="grid grid-cols-[100px_1fr_1fr] gap-4 mb-2 text-xs font-medium text-foreground-700">
              <span>日付</span>
              <span>🇯🇵 日本メンバー</span>
              <span>🇹🇭 タイメンバー</span>
            </div>
            <div className="space-y-1">
              {[
                { date: '7/18（金）', jp: '稼働日', th: '稼働日', jpOk: true, thOk: true },
                { date: '7/21（月）', jp: '海の日（祝日）', th: '稼働日', jpOk: false, thOk: true },
                { date: '7/28（月）', jp: '稼働日', th: '国王誕生日（祝日）', jpOk: true, thOk: false },
                { date: '8/11（月）', jp: '山の日（祝日）', th: '稼働日', jpOk: false, thOk: true },
                { date: '8/12（火）', jp: '稼働日', th: '母の日（祝日）', jpOk: true, thOk: false },
                { date: '8/13〜15', jp: 'お盆休暇', th: '稼働日', jpOk: false, thOk: true },
                { date: '8/18（月）', jp: '稼働日', th: '稼働日', jpOk: true, thOk: true },
              ].map((day) => (
                <div key={day.date} className="grid grid-cols-[100px_1fr_1fr] gap-4 p-2 rounded text-xs border border-background-200/70">
                  <span className="font-medium text-foreground-800">{day.date}</span>
                  <span className={day.jpOk ? 'text-green-700' : 'text-amber-700'}>{day.jp}</span>
                  <span className={day.thOk ? 'text-green-700' : 'text-amber-700'}>{day.th}</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-foreground-500 mt-4">※ メンバーごとに所属国を設定することで、各国の祝日と稼働日が自動でカレンダーに反映されます。</p>
          </div>
        </div>
      </div>

      <section>
        <h2>国別祝日を考慮したスケジュール管理</h2>
        <p>メンバーごとに所属国や利用言語を設定できます。AIが各メンバーの国の祝日や休業日を考慮し、以下のようなスケジュール管理が可能です。</p>
        <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-4 my-4">
          {[
            { icon: 'ri-calendar-check-line', title: '国別の休日設定', desc: '日本の祝日には日本メンバーを休みにし、タイの祝日にはタイメンバーを休みにする設定が可能です。' },
            { icon: 'ri-calendar-event-line', title: '稼働日ベースの会議設定', desc: '両国が稼働している日のみを会議やマイルストーンの候補日として表示します。' },
            { icon: 'ri-time-line', title: '期限の自動計算', desc: 'メンバーの稼働日をもとに、AIが期限や必要日数を自動計算します。' },
            { icon: 'ri-sun-line', title: '長期休暇の反映', desc: 'お盆、年末年始、ソンクラーン（タイ正月）など、国別の長期休暇をカレンダーに反映します。' },
          ].map((item) => (
            <div key={item.title} className="flex items-start gap-3 bg-background-100 rounded-lg p-4 border border-background-200/70">
              <div className="w-9 h-9 bg-secondary-100 rounded-lg flex items-center justify-center shrink-0">
                <i className={`${item.icon} text-secondary-600 text-sm`}></i>
              </div>
              <div>
                <h3 className="text-sm font-bold text-foreground-900">{item.title}</h3>
                <p className="text-xs text-foreground-600">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2>メンバーごとの言語設定</h2>
        <p>各メンバーは自分の使いやすい言語で”おもいむすび”を操作できます。UI表示言語と、AI日報の解析言語はメンバーごとに独立して設定可能です。例えば、日本人メンバーが日本語UIで操作している横で、タイ人メンバーがタイ語UIで同じプロジェクトを確認できます。</p>
        <p>日報のAI解析も、書かれた言語を自動判別して処理するため、メンバーは自分の使いやすい言語で報告できます。</p>
      </section>

      <section>
        <h2>よくある質問</h2>
        <h3>将来的に対応言語は増えますか</h3>
        <p>現在は日本語・英語・タイ語の3言語に対応しています。中国語、ベトナム語、インドネシア語など、他のアジア言語への対応も検討中です。最新情報はお問い合わせください。</p>
        <h3>日本の祝日カレンダーは自動更新されますか</h3>
        <p>はい。祝日法の改正や振替休日にも対応し、祝日カレンダーは自動で最新の状態に更新されます。</p>
      </section>
    </PageShell>
  );
}